<template>
    <!-- Auth redesign — split-карточка (бренд-панель + форма). Макет Claude Design.
         Бренд-панель: текстура и акцент выбранной темы.
         Лого — в бренд-панели (слот brand-logo), на мобильном — в светлом хедере. -->
    <div class="auth-split" :class="{ 'runtime-brand': $store.state.individual === 'Zentrolink' }">
        <DemoBanner @change="testEnvironment = $event" />
        <div class="split-card">
            <aside class="brand-panel">
                <div class="bp-top">
                    <span class="bp-logo"><slot name="brand-logo" /></span>
                    <slot name="lang" />
                </div>
                <div class="bp-mid">
                    <span class="bp-eyebrow">{{ brand.eyebrow }}</span>
                    <h2 class="bp-headline">{{ brand.headline }}</h2>
                    <p class="bp-lead">{{ brand.lead }}</p>
                </div>
                <ul class="bp-trust">
                    <li v-for="(t, i) in trust" :key="i" class="bp-trust-item">
                        <span class="bp-tdot" v-html="ICONS[i % ICONS.length]"></span>{{ t }}
                    </li>
                </ul>
            </aside>
            <section class="split-form">
                <div class="split-form-inner">
                    <slot />
                    <AuthFooter :test-environment="testEnvironment" />
                </div>
            </section>
        </div>
    </div>
</template>

<script>
import AuthFooter from '@/components/service/AuthFooter';
import DemoBanner from '@/components/service/DemoBanner';

export default {
    components: { AuthFooter, DemoBanner },
    props: {
        // { eyebrow, headline, lead }
        brand: { type: Object, default: () => ({ eyebrow: '', headline: '', lead: '' }) },
        // массив подписей trust-строк (иконки фиксированы по порядку: shield / key / globe)
        trust: { type: Array, default: () => [] },
    },
    data() {
        return {
            testEnvironment: false,
            // Lucide-иконки (lucide-vue-next), по смыслу trust-строк (реальные фичи кабинета):
            // lock → шифрование соединения · shield-check → подтверждение операций · headset → поддержка
            // (2FA на вход в кабинете НЕТ — клейм убран)
            ICONS: [
                '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect width="18" height="11" x="3" y="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>',
                '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z"/><path d="m9 12 2 2 4-4"/></svg>',
                '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 11h3a2 2 0 0 1 2 2v3a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-5Zm0 0a9 9 0 1 1 18 0m0 0v5a2 2 0 0 1-2 2h-1a2 2 0 0 1-2-2v-3a2 2 0 0 1 2-2h3Z"/><path d="M21 16v2a4 4 0 0 1-4 4h-5"/></svg>',
            ],
        };
    },
};
</script>

<style lang="scss" scoped>
// Full-screen: белая страница на весь экран, бренд-панель — скруглённый
// прямоугольник с белым отступом-«рамкой» вокруг, справа — форма.
.auth-split { width: 100%; min-height: 100vh; background: var(--card); }

.split-card {
    width: 100%;
    min-height: 100vh;
    background: var(--card);
    display: grid;
    grid-template-columns: 0.92fr 1fr;
}

/* ---------- бренд-панель (синий прямоугольник в белой рамке) ---------- */
.brand-panel {
    position: relative;
    overflow: hidden;
    color: #fff;
    margin: 10px;              // белая рамка вокруг панели
    border-radius: 26px;
    padding: 44px;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    background: #14163a url($bg-image) center / cover no-repeat;
}
.brand-panel::before {
    content: "";
    position: absolute;
    inset: 0;
    z-index: 1;
    background: linear-gradient(155deg,
        color-mix(in srgb, var(--accent) 42%, transparent) 0%,
        rgba(16, 18, 46, .34) 45%,
        rgba(10, 11, 30, .74) 100%);
}
.brand-panel > * { position: relative; z-index: 2; }

.bp-top { display: flex; align-items: center; justify-content: space-between; }
.bp-logo { display: inline-flex; align-items: center; }
// реальный лого бренда (store.logo / logoTablet с бэка) — как в старой теме, без бело-фильтра
.bp-logo :deep(img) { max-height: 34px; width: auto; display: block; }

.bp-mid { margin-top: 36px; }
.bp-eyebrow {
    font-size: 13px; font-weight: 600; letter-spacing: .04em;
    text-transform: uppercase; color: rgba(255, 255, 255, .72);
}
.bp-headline {
    font-family: 'HelveticaNeue bold', 'Roboto bold', sans-serif;
    font-size: 34px; line-height: 1.12; font-weight: 700; letter-spacing: -0.02em;
    margin: 14px 0 16px; text-wrap: balance;
}
.bp-lead { font-size: 15px; line-height: 1.6; color: rgba(255, 255, 255, .82); margin: 0; max-width: 32ch; }

.bp-trust { list-style: none; margin: auto 0 0; padding: 0; display: flex; flex-direction: column; gap: 13px; }
.bp-trust-item { display: flex; align-items: center; gap: 11px; font-size: 13.5px; color: rgba(255, 255, 255, .88); }
.bp-tdot {
    width: 30px; height: 30px; border-radius: 9px; flex: none;
    background: rgba(255, 255, 255, .14); display: grid; place-items: center;
}
.bp-tdot :deep(svg) { width: 15px; height: 15px; }

/* ---------- сторона формы ---------- */
.split-form { padding: 56px; display: flex; flex-direction: column; justify-content: center; min-width: 0; }
.split-form-inner { width: 100%; max-width: 380px; margin: 0 auto; }

/* ---------- адаптив ---------- */
@media (max-width: 900px) {
    // 1 колонка: хедер по контенту, форма — на остаток (иначе grid растягивает хедер на полэкрана)
    .split-card { grid-template-columns: 1fr; grid-template-rows: auto 1fr; }
    // мобила: убираем цветную карточку — панель = плоский белый хедер (лого + язык)
    .brand-panel {
        margin: 0; border-radius: 0; min-height: 0;
        padding: 20px 24px 0;
        background: none; color: var(--ink); overflow: visible;
    }
    .brand-panel::before { display: none; }
    .bp-mid, .bp-trust { display: none; }
    // языковая плашка — светлый вариант (не полупрозрачный on-dark)
    .brand-panel :deep(.lang.on-dark .lang-btn) {
        background: #fff; border-color: var(--line-strong); color: var(--ink); backdrop-filter: none;
    }
    .brand-panel :deep(.lang.on-dark .lang-btn:hover) { background: #f3f3f6; border-color: #bcc0cc; }
    .brand-panel :deep(.lang.on-dark .lang-btn .chev) { color: var(--ink-faint); }
    // контент под лого центрируем по вертикали (margin:auto — безопасно для скролла при высоком контенте)
    .split-form { padding: 22px 28px 46px; }
    .split-form-inner { margin: auto; }
}
@media (max-width: 540px) {
    .split-form-inner :deep(.form-title) { font-size: 26px; }
}
// Runtime names can contain 80 Unicode characters. Keep the grid and logo
// inside the viewport while retaining the complete accessible/visible name.
.auth-split.runtime-brand {
    .brand-panel { background-color: #9A3412; }
    .brand-panel::before { background: rgba(66, 20, 0, .34); }
    .bp-logo :deep(.company-logo) { width: 174px; height: 32px; max-height: none; }
    .bp-logo :deep(.runtime-brand-logo:has(.company-logo)) { background: #fff; color: var(--ink); border-radius: 8px; padding: 12px 16px; flex: none; }
    @media (max-width: 900px) {
        .brand-panel { background: none; }
        .bp-logo :deep(.runtime-brand-logo:has(.company-logo)) { padding: 0; }
    }
    .split-card { grid-template-columns: minmax(0, .92fr) minmax(0, 1fr); }
    .brand-panel, .split-form { min-width: 0; }
    .bp-top { gap: 16px; }
    .bp-logo { min-width: 0; flex: 1; }
    .bp-top :deep(.lang) { flex: none; }
    @media (max-width: 900px) {
        .split-card { grid-template-columns: minmax(0, 1fr); }
    }
}
</style>
